import styled, { css } from 'styled-components';
import PhoneInput from 'react-phone-number-input';
import { Checkbox, Dropdown, FieldBox, Input } from '@swordhealth/ui-corporate';
import FileUpload from '@/app/components/core/forms/input/FileUpload';
import 'react-phone-number-input/style.css';

import pt from 'react-phone-number-input/locale/pt';
import en from 'react-phone-number-input/locale/en';
import NextImage from 'next/image';
import React from 'react';

const Field = ({
  name,
  label,
  type,
  placeholder,
  helperMessage,
  options,
  formik,
  locale,
  clientSolutions,
  required = true,
  ...rest
}) => {
  switch (type) {
    case 'checkbox':
      return (
        <Checkbox
          name={name}
          label={label}
          onChange={(value) => formik.setFieldValue(name, value)}
          defaultChecked={formik.values[name]}
          value={formik.values[name]}
          helperMessage={helperMessage}
          errorMessage={formik.touched[name] && formik.errors[name]}
          className="checkbox-consent"
          variant={'minimal'}
          theme={'neutral'}
          {...rest}
        />
      );
    case 'dropdown':
      return (
        <Dropdown
          name={name}
          label={label}
          placeholder={placeholder}
          options={options}
          value={formik.values[name]}
          onChange={(value) => formik.setFieldValue(name, value)}
          helperMessage={helperMessage}
          errorMessage={formik.touched[name] && formik.errors[name]}
          {...rest}
        />
      );
    case 'file':
      return (
        <FileUpload
          name={name}
          label={label}
          placeholder={placeholder}
          value={formik.values[name]}
          minFiles={1}
          maxFiles={1}
          maxSize={5}
          multiple={false}
          formik={formik}
          helperMessage={helperMessage}
          errorMessage={formik.errors[name]}
          {...rest}
        />
      );
    case 'date':
      return (
        <StyledInput
          name={name}
          required={required}
          label={label}
          placeholder={placeholder}
          data-placeholder={placeholder}
          value={formik.values[name]}
          type={type}
          onChange={(value) => formik.setFieldValue(name, value)}
          helperMessage={helperMessage}
          errorMessage={formik.touched[name] && formik.errors[name]}
          $hasValue={formik.values[name]}
          {...rest}
        />
      );
    case 'tel':
      return (
        <FieldBox
          required={required}
          label={label}
          placeholder={placeholder}
          value={formik.values[name]}
          type={type}
          onChange={(value) => formik.setFieldValue(name, value)}
          helperMessage={helperMessage}
          errorMessage={formik.touched[name] && formik.errors[name]}
          {...rest}
        >
          <StyledPhoneInput
            name={name}
            labels={locale === 'pt' ? pt : en}
            international={false}
            defaultCountry="PT"
            value={formik.values[name]}
            onChange={(value) => formik.setFieldValue(name, value)}
          />
        </FieldBox>
      );
    case 'solutions-radio':
      if (clientSolutions.length === 1) return null;
      return (
        <StyledFieldBox
          required={required}
          label={label}
          placeholder={placeholder}
          value={formik.values[name]}
          type={type}
          onChange={(value) => formik.setFieldValue(name, value)}
          helperMessage={helperMessage}
          errorMessage={formik.touched[name] && formik.errors[name]}
          {...rest}
        >
          <SolutionWrapper>
            {clientSolutions.map((client, i) => {
              const isActive = formik.values.solution === client;
              return (
                <SolutionImageWrapper
                  key={`solution-image-${i}`}
                  onClick={() =>
                    formik.values.solution === client
                      ? formik.setFieldValue(name, '')
                      : formik.setFieldValue(name, client)
                  }
                  $isActive={isActive}
                >
                  <NextImage
                    src={`/logos/sub_brand_logo_${client}.svg`}
                    alt={client}
                    width={50}
                    height={16}
                  />
                </SolutionImageWrapper>
              );
            })}
          </SolutionWrapper>
        </StyledFieldBox>
      );
    default:
      return (
        <Input
          name={name}
          required={required}
          label={label}
          placeholder={placeholder}
          value={formik.values[name]}
          type={type}
          onChange={(value) => formik.setFieldValue(name, value)}
          helperMessage={helperMessage}
          errorMessage={formik.touched[name] && formik.errors[name]}
          {...rest}
        />
      );
  }
};

export default Field;

const StyledInput = styled(Input)`
  input {
    height: 30px;
    ${(props) =>
      props.$hasValue &&
      css`
        color: ${(props) => props.theme.colors.grey.dark} !important;
      `};

    /* Hides browser's native placeholder */
    &::-webkit-datetime-edit {
      opacity: 0 !important;
    }

    &::-webkit-datetime-edit-text {
      opacity: 1 !important;
    }

    /* Makes it visible again on focus and when there's a valid date */
    &:focus::-webkit-datetime-edit {
      opacity: 1 !important;
    }

    &:valid::-webkit-datetime-edit {
      opacity: 1 !important;
    }

    /* Fake placeholder */
    &::before {
      content: attr(data-placeholder);
      color: ${(props) => props.theme.colors.grey.default};
      position: absolute;
    }
    &:focus::before {
      content: '';
    }
    &:valid::before {
      content: '';
    }
  }

  @media (max-width: 1279px) {
    input {
      height: 30px;

      ${(props) =>
        props.$hasValue &&
        css`
          width: fit-content;
        `};
    }
  }
`;

const StyledPhoneInput = styled(PhoneInput)`
  input {
    border: none;
  }
`;

const SolutionWrapper = styled.div`
  position: relative;
  display: flex;
  gap: 12px;

  @media (max-width: ${(props) => props.theme.breakpoints.max_sm}px) {
    display: grid;
    grid-template-columns: repeat(2, 1fr);

    :nth-child(2n + 1):last-child {
      grid-column: 1 / -1;
    }
  }
`;

const SolutionImageWrapper = styled.div`
  position: relative;
  width: 100%;
  padding: 24px 32px;
  background-color: var(--ui-colors-white);
  border-radius: 16px;
  text-align: center;
  border: ${({ $isActive }) => ($isActive ? `1px solid #5993EA` : '1px solid white')};
  cursor: pointer;

  &:hover {
    background-color: var(--ui-colors-primary-50);
    border: 1px solid var(--ui-colors-primary-500);
  }

  img {
    height: 16px;
    width: auto;
    max-width: 100%;
  }
`;

const StyledFieldBox = styled(FieldBox)`
  & > div {
    padding: 0;
    border: none;
    background-color: transparent;
  }
`;
